import React from 'react';
import { Grid3x3, Maximize2, Package, Percent } from 'lucide-react';

const CuttingPlanBoardsPreview = ({ boards = [], positions = [], loading }) => {
  const getPositionsForBoard = (boardId) => {
    return positions.filter(pos => pos.board_id === boardId);
  };

  const calculateEfficiency = (board) => {
    const totalArea = (parseFloat(board.width) * parseFloat(board.height)) / 1000000;
    const usedArea = parseFloat(board.used_area) || 0;
    if (totalArea === 0) return 0;
    return ((usedArea / totalArea) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!boards || boards.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Chapas do Plano</h2>
        <div className="text-center py-8">
          <Grid3x3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nenhuma chapa adicionada neste plano</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Chapas do Plano ({boards.length})
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {boards.map((board) => {
          const boardPositions = getPositionsForBoard(board.id);
          const efficiency = calculateEfficiency(board);

          return (
            <div
              key={board.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <Grid3x3 className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {board.board_code || `Chapa ${board.id}`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {board.width} x {board.height}mm
                    </p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  parseFloat(efficiency) >= 70
                    ? 'bg-emerald-50 text-emerald-700'
                    : parseFloat(efficiency) >= 50
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  <Percent className="w-3 h-3 mr-1" />
                  {efficiency}%
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Material:</span>
                  <span className="text-gray-900 font-medium">{board.material_name || '-'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Peças:</span>
                  <span className="text-gray-900 font-medium">
                    <Package className="w-3 h-3 inline mr-1" />
                    {boardPositions.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Área util:</span>
                  <span className="text-gray-900 font-medium">
                    <Maximize2 className="w-3 h-3 inline mr-1" />
                    {parseFloat(board.used_area || 0).toFixed(3)} m²
                  </span>
                </div>
              </div>

              {boardPositions.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Peças nesta chapa:</p>
                  <div className="flex flex-wrap gap-1">
                    {boardPositions.slice(0, 5).map((pos) => (
                      <span
                        key={pos.id}
                        className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                        title={pos.part_code}
                      >
                        {pos.part_code}
                      </span>
                    ))}
                    {boardPositions.length > 5 && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        +{boardPositions.length - 5} mais
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CuttingPlanBoardsPreview;
